import React, { createContext, useContext, useState, useEffect } from 'react';
import { format as dateFnsFormat, formatInTimeZone } from 'date-fns-tz';
import { useAuth } from './AuthContext';
import { tenantSettingsApi } from '../services/api';

const SettingsContext = createContext();

// Common timezone options
export const TIMEZONE_OPTIONS = [
  { value: 'Pacific/Auckland', label: '(GMT+12:00) Auckland, Wellington' },
  { value: 'Australia/Sydney', label: '(GMT+10:00) Sydney, Melbourne' },
  { value: 'Australia/Brisbane', label: '(GMT+10:00) Brisbane' },
  { value: 'Australia/Adelaide', label: '(GMT+09:30) Adelaide' },
  { value: 'Australia/Perth', label: '(GMT+08:00) Perth' },
  { value: 'Asia/Singapore', label: '(GMT+08:00) Singapore' },
  { value: 'Asia/Hong_Kong', label: '(GMT+08:00) Hong Kong' },
  { value: 'Asia/Tokyo', label: '(GMT+09:00) Tokyo' },
  { value: 'Asia/Dubai', label: '(GMT+04:00) Dubai' },
  { value: 'Europe/London', label: '(GMT+00:00) London' },
  { value: 'Europe/Paris', label: '(GMT+01:00) Paris, Berlin' },
  { value: 'America/New_York', label: '(GMT-05:00) New York' },
  { value: 'America/Chicago', label: '(GMT-06:00) Chicago' },
  { value: 'America/Denver', label: '(GMT-07:00) Denver' },
  { value: 'America/Los_Angeles', label: '(GMT-08:00) Los Angeles' },
  { value: 'UTC', label: '(GMT+00:00) UTC' },
];

// Date format options
export const DATE_FORMAT_OPTIONS = [
  { value: 'dd/MM/yyyy', label: 'DD/MM/YYYY (31/12/2024)' },
  { value: 'MM/dd/yyyy', label: 'MM/DD/YYYY (12/31/2024)' },
  { value: 'yyyy-MM-dd', label: 'YYYY-MM-DD (2024-12-31)' },
  { value: 'dd MMM yyyy', label: 'DD MMM YYYY (31 Dec 2024)' },
  { value: 'MMMM d, yyyy', label: 'Month D, YYYY (December 31, 2024)' },
];

// Time format options
export const TIME_FORMAT_OPTIONS = [
  { value: 'HH:mm', label: '24-hour (14:30)' },
  { value: 'hh:mm a', label: '12-hour (02:30 PM)' },
];

const DEFAULT_SETTINGS = {
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  dateFormat: 'dd/MM/yyyy',
  timeFormat: 'HH:mm',
  companyName: '',
  companyAddress: '',
  companyPhone: '',
  companyEmail: '',
  companyLogo: null,
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// Date-only strings ("2026-10-05") are calendar dates: print as-is, never shift by time zone.
export const formatZoned = (date, timeZone, fmt) => {
  if (typeof date === 'string' && DATE_ONLY.test(date)) {
    const [y, m, d] = date.split('-').map(Number);
    return dateFnsFormat(new Date(y, m - 1, d), fmt);
  }
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return formatInTimeZone(dateObj, timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', fmt);
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('equipr-settings');
    if (saved) {
      try {
        return { ...DEFAULT_SETTINGS, ...JSON.parse(saved) };
      } catch {
        return DEFAULT_SETTINGS;
      }
    }
    return DEFAULT_SETTINGS;
  });

  useEffect(() => {
    localStorage.setItem('equipr-settings', JSON.stringify(settings));
  }, [settings]);

  // Dates show in the company's time zone (the same one the backend uses), not the browser's.
  const { token } = useAuth();
  const [companyTz, setCompanyTz] = useState(null);
  useEffect(() => {
    if (!token) { setCompanyTz(null); return; }
    tenantSettingsApi.getRegional().then((r) => setCompanyTz(r.data?.timezone || null)).catch(() => {});
  }, [token]);
  const timezone = companyTz || settings.timezone;

  const updateSettings = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  // Format date according to user's timezone and format preference
  const formatDate = (date, formatStr = null) => {
    if (!date) return '-';
    try {
      return formatZoned(date, timezone, formatStr || settings.dateFormat);
    } catch {
      return '-';
    }
  };

  // Format datetime according to user's timezone and format preferences
  const formatDateTime = (date) => {
    if (!date) return '-';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      const fmt = `${settings.dateFormat} ${settings.timeFormat}`;
      return formatInTimeZone(dateObj, timezone, fmt);
    } catch {
      return '-';
    }
  };

  // Format time only
  const formatTime = (date) => {
    if (!date) return '-';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return formatInTimeZone(dateObj, timezone, settings.timeFormat);
    } catch {
      return '-';
    }
  };

  return (
    <SettingsContext.Provider value={{
      settings: { ...settings, timezone },
      updateSettings,
      formatDate,
      formatDateTime,
      formatTime,
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
